import React, { useState, useMemo } from 'react';
import styles from 'Components/PdfFormTemplates/PdfFormEditor/pdfFormEditor.module.css';
import { TokenGroup } from '../types';

interface Props {
  value: string;
  onChange: (value: string) => void;
  tokenGroups: TokenGroup[];
  tokenFilter?: (token: { label: string; value: string; type: string }) => boolean;
  hideCustomName?: boolean;
}

export function TokenPicker({ value, onChange, tokenGroups, tokenFilter, hideCustomName }: Props) {
  const [search, setSearch] = useState('');

  const baseGroups = useMemo(() => {
    if (!tokenFilter) return tokenGroups;
    return tokenGroups.map((group) => ({
      ...group,
      tokens: group.tokens.filter(tokenFilter),
    })).filter((g) => g.tokens.length > 0);
  }, [tokenGroups, tokenFilter]);

  const isToken = useMemo(
    () => baseGroups.some((g) => g.tokens.some((t) => t.value === value)),
    [value, baseGroups]
  );

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return baseGroups;
    const q = search.toLowerCase();
    return baseGroups.map((group) => ({
      ...group,
      tokens: group.tokens.filter((t) => t.label.toLowerCase().includes(q)),
    })).filter((g) => g.tokens.length > 0);
  }, [search, baseGroups]);

  return (
    <div className={styles.tokenPicker}>
      <input
        type="text"
        className={styles.tokenSearch}
        placeholder="Search tokens..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className={styles.tokenList}>
        {filteredGroups.map((group) => (
          <div key={group.label} className={styles.tokenGroup}>
            <div className={styles.tokenGroupLabel}>{group.label}</div>
            <div className={styles.tokenChips}>
              {group.tokens.map((token) => (
                <button
                  key={token.value}
                  type="button"
                  className={`${styles.tokenChip} ${value === token.value ? styles.tokenChipActive : ''}`}
                  onClick={() => onChange(token.value)}
                >
                  {token.label}
                </button>
              ))}
            </div>
          </div>
        ))}
        {filteredGroups.length === 0 && (
          <div className={styles.tokenEmpty}>No matching tokens</div>
        )}
      </div>
      {!hideCustomName && (
        <>
          <div className={styles.tokenDivider}>
            <span>or</span>
          </div>
          <input
            type="text"
            className={styles.customNameField}
            placeholder="Enter custom field name..."
            value={isToken ? '' : value}
            onChange={(e) => onChange(e.target.value)}
          />
        </>
      )}
    </div>
  );
}
